import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useMyTenant } from '../hooks/useMyTenant';
import { apiFetch } from '../lib/api';
import i18n from '../i18n';
import {
  detectCurrency,
  formatTierPrice,
  isPriceableTier,
} from '../lib/currency';
import { HelpPopover } from '../components/help';

// ── Types ─────────────────────────────────────────────────────────────────

interface BillingInvoice {
  id: string;
  date: string;
  description: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending';
}

interface BillingSummary {
  // CFG.BILL.A2 — gstNumber is the Indian GSTIN on file for the partner
  // tenant. Editable in the Tax Information section below; shown read-only
  // in the Current Plan card. null when the tenant has not provided one.
  plan: { tier: string; name: string; billingState: string; gstNumber: string | null };
  seats: { active: number; limit: number | null };
  usage: {
    periodStart: string;
    periodEnd: string;
    tokensUsed: number;
    agentSessions: number;
  };
  invoices: BillingInvoice[];
  paymentIntegration: { enabled: false; message: string };
}

// ── Style tokens ──────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-sm)',
  padding: 24,
  marginBottom: 16,
};

const sectionTitleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: 4,
};

const sectionSubtitleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  color: 'var(--text-secondary)',
  marginBottom: 16,
};

const statLabelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  marginBottom: 4,
};

const statValueStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 24,
  fontWeight: 700,
  color: 'var(--text)',
  lineHeight: 1.1,
};

// ── Helpers ───────────────────────────────────────────────────────────────

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return iso;
  }
}

function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function TierBadge({ name }: { name: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        fontFamily: 'var(--font-body)',
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--primary)',
        background: 'rgba(124,58,237,0.1)',
        border: '1px solid var(--primary)',
        borderRadius: 'var(--radius-full)',
      }}
    >
      {name}
    </span>
  );
}

function StatusBadge({ status }: { status: 'paid' | 'pending' }) {
  const isPaid = status === 'paid';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        fontFamily: 'var(--font-body)',
        fontSize: 11,
        fontWeight: 600,
        color: isPaid ? 'var(--success)' : 'var(--warning)',
        background: isPaid ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
        border: `1px solid ${isPaid ? 'var(--success)' : 'var(--warning)'}`,
        borderRadius: 'var(--radius-full)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    >
      {status}
    </span>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

// CFG.BILL.A2 — Indian GSTIN format (mirrors api/src/routes/tenants.ts).
const GST_NUMBER_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '8px 12px',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  color: 'var(--text)',
  background: 'var(--surface-alt)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  outline: 'none',
  boxSizing: 'border-box',
};

export default function BillingPage() {
  const { myTenant, loading: tenantLoading } = useMyTenant();
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  // Initialize to `true` so the first render already reflects the in-flight
  // fetch state. Setting this synchronously inside the effect body would
  // trigger the react-hooks/cascading-renders lint rule.
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── GST editing state ──────────────────────────────────────────────────
  const [gstNumber, setGstNumber] = useState('');
  const [gstError, setGstError] = useState('');
  const [gstSaving, setGstSaving] = useState(false);
  const [gstSaveMsg, setGstSaveMsg] = useState('');

  // Gate on partner membership, not tier. No tenant runs on PLATFORM_PARTNER
  // in production — partners are identified by partner_accounts membership,
  // surfaced via /tenants/me/membership → isPartner.
  const isPartner = myTenant?.isPartner === true;

  useEffect(() => {
    if (!isPartner) return;
    let cancelled = false;
    apiFetch<BillingSummary>('/api/billing/summary')
      .then((data) => {
        if (!cancelled) {
          setSummary(data);
          setGstNumber(data.plan.gstNumber ?? '');
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Failed to load billing summary');
      })
      .finally(() => {
        if (!cancelled) setLoadingSummary(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isPartner]);

  const tenantId = myTenant?.tenantId;

  const validateGst = (value: string): string => {
    const trimmed = value.trim();
    if (trimmed && !GST_NUMBER_REGEX.test(trimmed)) {
      return 'Invalid GST number format. Expected 15 chars, e.g. 27AAPFU0939F1ZV';
    }
    return '';
  };

  const handleGstBlur = () => {
    setGstError(validateGst(gstNumber));
  };

  const handleGstSave = async () => {
    if (!tenantId) return;
    const err = validateGst(gstNumber);
    if (err) {
      setGstError(err);
      return;
    }
    setGstError('');
    setGstSaving(true);
    setGstSaveMsg('');
    try {
      const trimmed = gstNumber.trim();
      await apiFetch(`/tenants/${tenantId}`, {
        method: 'PATCH',
        body: JSON.stringify({ gstNumber: trimmed }),
      });
      // Update the read-only display in the Current Plan card to stay in sync.
      if (summary) {
        setSummary({
          ...summary,
          plan: { ...summary.plan, gstNumber: trimmed || null },
        });
      }
      setGstSaveMsg('GST number saved');
      setTimeout(() => setGstSaveMsg(''), 3000);
    } catch {
      setGstSaveMsg('Failed to save');
    } finally {
      setGstSaving(false);
    }
  };

  // Still resolving tenant — show skeleton
  if (tenantLoading) {
    return (
      <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
        <Sidebar />
        <main className="flex-1 px-8 py-6" data-testid="billing-loading">
          <div style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}>
            Loading…
          </div>
        </main>
      </div>
    );
  }

  // Non-partner tiers — redirect away
  if (!isPartner) {
    return <Navigate to="/projects" replace />;
  }

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
      <Sidebar />
      <main className="flex-1 px-8 py-6">
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 24,
            fontWeight: 700,
            color: 'var(--text)',
            marginBottom: 24,
          }}
        >
          Billing
        </h1>
        <div style={{ maxWidth: 960 }}>

          {loadingSummary && (
            <div
              data-testid="billing-summary-loading"
              style={{
                ...cardStyle,
                fontFamily: 'var(--font-body)',
                color: 'var(--text-secondary)',
              }}
            >
              Loading billing summary…
            </div>
          )}

          {error && !loadingSummary && (
            <div
              role="alert"
              style={{
                ...cardStyle,
                borderColor: 'var(--error)',
                color: 'var(--error)',
                fontFamily: 'var(--font-body)',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {summary && !loadingSummary && !error && (
            <>
              {/* Plan */}
              <section style={cardStyle} data-testid="billing-plan-card">
                <h2 style={sectionTitleStyle}>Current plan</h2>
                <p style={sectionSubtitleStyle}>Your subscription and billing status.</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div>
                    <div style={statLabelStyle}>Plan</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={statValueStyle}>{summary.plan.name}</span>
                      <TierBadge name={summary.plan.tier} />
                    </div>
                  </div>
                  {/* CFG.I18N.A2 — region-aware price. Only rendered when
                      the server tier is STARTER or GROWTH. INDIVIDUAL,
                      ENTERPRISE, and PLATFORM_PARTNER have no published
                      marketing price and stay silent — the plan name +
                      tier badge above still identify the plan, just
                      without a numeric price row. */}
                  {isPriceableTier(summary.plan.tier) && (
                    <div data-testid="billing-price-row">
                      <div style={statLabelStyle}>Price</div>
                      <div
                        style={{
                          fontFamily: 'var(--font-body)',
                          fontSize: 14,
                          fontWeight: 600,
                          color: 'var(--text)',
                        }}
                      >
                        {formatTierPrice(
                          summary.plan.tier,
                          detectCurrency({
                            locale: i18n.language,
                            timezone: myTenant?.timezone ?? null,
                          }),
                        )}
                      </div>
                    </div>
                  )}
                  <div style={{ marginLeft: 'auto' }}>
                    <div style={statLabelStyle}>Billing state</div>
                    <div
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: 14,
                        fontWeight: 600,
                        color: 'var(--text)',
                      }}
                    >
                      {summary.plan.billingState}
                    </div>
                  </div>
                </div>
                {/* GST identifier — CFG.BILL.A2. Hidden when not set. */}
                {summary.plan.gstNumber && (
                  <div style={{ marginTop: 16 }} data-testid="billing-gst-row">
                    <div style={statLabelStyle}>GST</div>
                    <div
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: 14,
                        fontWeight: 600,
                        color: 'var(--text)',
                        letterSpacing: 0.3,
                      }}
                    >
                      {summary.plan.gstNumber}
                    </div>
                  </div>
                )}
              </section>

              {/* Tax Information — editable GST field (CFG.BILL.A2) */}
              <section style={cardStyle} data-testid="billing-tax-card">
                <h2 style={sectionTitleStyle}>Tax information</h2>
                <p style={sectionSubtitleStyle}>
                  Your GST identification number for Indian tax compliance on invoices.
                </p>
                <div style={{ maxWidth: 400 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label
                      htmlFor="billing-gst-input"
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: 13,
                        fontWeight: 500,
                        color: 'var(--text)',
                      }}
                    >
                      GST Number
                    </label>
                    <HelpPopover
                      id="gst-number-format"
                      heading="GST Number"
                      // Custom aria-label avoids collision with existing
                      // BillingPage tests that use getByLabelText(/GST Number/i)
                      // to find the input — the auto-derived aria-label
                      // would otherwise be "More information: GST Number"
                      // and getByLabelText matches both label-htmlFor and
                      // aria-label.
                      ariaLabel="Help with the GST field"
                      body={
                        <>
                          <p style={{ margin: '0 0 8px' }}>
                            Indian businesses registered for GST need to provide their 15-character GSTIN here for tax-compliant invoicing. Format example: 27AAPFU0939F1ZV.
                          </p>
                          <p style={{ margin: '0 0 8px' }}>
                            If you&apos;re outside India or not GST-registered, leave this blank. Your invoices will be issued without a GST number, which is correct for non-registered entities.
                          </p>
                          <p style={{ margin: 0 }}>
                            You can find your GSTIN on your GST registration certificate or on the GST portal under &ldquo;View Profile.&rdquo;
                          </p>
                        </>
                      }
                    />
                  </div>
                  <input
                    id="billing-gst-input"
                    style={{
                      ...inputStyle,
                      borderColor: gstError ? 'var(--error)' : 'var(--border)',
                    }}
                    value={gstNumber}
                    onChange={e => {
                      setGstNumber(e.target.value);
                      if (gstError) setGstError('');
                    }}
                    onBlur={handleGstBlur}
                    placeholder="27AAPFU0939F1ZV"
                    autoComplete="off"
                    spellCheck={false}
                    aria-invalid={gstError ? 'true' : 'false'}
                    aria-describedby={'billing-gst-help' + (gstError ? ' billing-gst-error' : '')}
                  />
                  <p
                    id="billing-gst-help"
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      marginTop: 6,
                    }}
                  >
                    15-character alphanumeric Indian GSTIN. Leave blank to clear.
                  </p>
                  {gstError && (
                    <p
                      id="billing-gst-error"
                      role="alert"
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: 12,
                        color: 'var(--error)',
                        marginTop: 4,
                      }}
                    >
                      {gstError}
                    </p>
                  )}
                  <div style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      disabled={gstSaving}
                      onClick={() => void handleGstSave()}
                      style={{
                        padding: '8px 16px',
                        fontFamily: 'var(--font-display)',
                        fontSize: 13,
                        fontWeight: 600,
                        color: 'white',
                        background: 'var(--primary)',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        cursor: gstSaving ? 'not-allowed' : 'pointer',
                        opacity: gstSaving ? 0.6 : 1,
                      }}
                    >
                      {gstSaving ? 'Saving…' : 'Save GST Number'}
                    </button>
                    {gstSaveMsg && (
                      <span
                        style={{
                          marginLeft: 12,
                          fontFamily: 'var(--font-body)',
                          fontSize: 13,
                          fontWeight: 500,
                          color: gstSaveMsg.includes('Failed') ? 'var(--error)' : 'var(--success)',
                        }}
                      >
                        {gstSaveMsg}
                      </span>
                    )}
                  </div>
                </div>
              </section>

              {/* Seats + usage */}
              <section
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 16,
                  marginBottom: 16,
                }}
              >
                <div style={{ ...cardStyle, marginBottom: 0 }} data-testid="billing-seats-card">
                  <div style={statLabelStyle}>Seats</div>
                  <div style={statValueStyle}>
                    {summary.seats.active}
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'var(--text-secondary)',
                        marginLeft: 6,
                      }}
                    >
                      / {summary.seats.limit ?? 'unlimited'}
                    </span>
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      marginTop: 4,
                    }}
                  >
                    Active tenant members
                  </div>
                </div>

                <div style={{ ...cardStyle, marginBottom: 0 }} data-testid="billing-tokens-card">
                  <div style={statLabelStyle}>Tokens used this month</div>
                  <div style={statValueStyle}>{formatTokens(summary.usage.tokensUsed)}</div>
                  <div
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      marginTop: 4,
                    }}
                  >
                    Resets {formatDate(summary.usage.periodEnd)}
                  </div>
                </div>

                <div style={{ ...cardStyle, marginBottom: 0 }} data-testid="billing-sessions-card">
                  <div style={statLabelStyle}>Agent sessions this month</div>
                  <div style={statValueStyle}>{summary.usage.agentSessions}</div>
                  <div
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      marginTop: 4,
                    }}
                  >
                    Since {formatDate(summary.usage.periodStart)}
                  </div>
                </div>
              </section>

              {/* Invoice history */}
              <section style={cardStyle} data-testid="billing-invoices-card">
                <h2 style={sectionTitleStyle}>Invoice history</h2>
                <p style={sectionSubtitleStyle}>
                  Display-only preview based on monthly usage. Real invoicing arrives with the payment integration.
                </p>
                {summary.invoices.length === 0 ? (
                  <div
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 13,
                      color: 'var(--text-secondary)',
                      padding: '12px 0',
                    }}
                  >
                    No invoices yet.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontFamily: 'var(--font-body)',
                        fontSize: 13,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            borderBottom: '1px solid var(--border)',
                            color: 'var(--text-secondary)',
                            textAlign: 'left',
                          }}
                        >
                          <th style={{ padding: '8px 12px 8px 0', fontWeight: 600 }}>Date</th>
                          <th style={{ padding: '8px 12px', fontWeight: 600 }}>Description</th>
                          <th style={{ padding: '8px 12px', fontWeight: 600, textAlign: 'right' }}>
                            Amount
                          </th>
                          <th style={{ padding: '8px 0 8px 12px', fontWeight: 600 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.invoices.map((inv) => (
                          <tr
                            key={inv.id}
                            style={{
                              borderBottom: '1px solid var(--border)',
                              color: 'var(--text)',
                            }}
                          >
                            <td style={{ padding: '12px 12px 12px 0' }}>{formatDate(inv.date)}</td>
                            <td style={{ padding: '12px' }}>{inv.description}</td>
                            <td style={{ padding: '12px', textAlign: 'right' }}>
                              {formatAmount(inv.amount, inv.currency)}
                            </td>
                            <td style={{ padding: '12px 0 12px 12px' }}>
                              <StatusBadge status={inv.status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Payment integration placeholder */}
              <section
                style={{
                  ...cardStyle,
                  borderStyle: 'dashed',
                  background: 'var(--surface-alt)',
                }}
                data-testid="billing-payment-placeholder"
              >
                <h2 style={sectionTitleStyle}>Payment method</h2>
                <p style={sectionSubtitleStyle}>{summary.paymentIntegration.message}</p>
                <button
                  type="button"
                  disabled
                  style={{
                    padding: '8px 16px',
                    fontFamily: 'var(--font-display)',
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    background: 'var(--surface)',
                    border: '1px dashed var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'not-allowed',
                  }}
                >
                  Manage payment method (coming soon)
                </button>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
